import React, { useState, useEffect } from 'react';
import { logisticsStatsAPI } from '@/lib/api';
import { 
  Truck, Package, CheckCircle2, AlertTriangle, TrendingDown,
  Calendar, MapPin, BarChart3, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format, subDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];

const AdminLogisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [summary, setSummary] = useState(null);
  const [locationStats, setLocationStats] = useState([]);
  const [topShortages, setTopShortages] = useState([]);
  const [recentRoutes, setRecentRoutes] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, locationRes, shortagesRes, routesRes] = await Promise.all([
        logisticsStatsAPI.getSummary(startDate, endDate),
        logisticsStatsAPI.getByLocation(startDate, endDate),
        logisticsStatsAPI.getTopShortages(startDate, endDate, 10),
        logisticsStatsAPI.getRecentRoutes(20)
      ]);
      
      setSummary(summaryRes.data);
      setLocationStats(locationRes.data);
      setTopShortages(shortagesRes.data);
      setRecentRoutes(routesRes.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData();
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd MMM, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  // Datos para el gráfico de pie (entregas completas vs parciales)
  const deliveryPieData = summary ? [
    { name: 'Completas', value: summary.complete_deliveries, color: '#22c55e' },
    { name: 'Parciales', value: summary.partial_deliveries, color: '#f59e0b' }
  ] : [];

  // Datos para el gráfico de barras por local
  const locationBarData = locationStats.map(loc => ({
    name: loc.location_name.replace('Local ', ''),
    completas: loc.complete_deliveries,
    parciales: loc.partial_deliveries,
    cumplimiento: loc.average_compliance
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-logistics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Logística</h1>
          <p className="text-muted-foreground mt-1">Estadísticas de entregas y faltantes</p>
        </div>
        
        {/* Filtros de fecha */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-36"
            />
            <span className="text-slate-400">-</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-36"
            />
          </div>
          <Button onClick={handleFilter} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Filtrar
          </Button>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Truck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Rutas</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.total_routes || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Entregas Completas</p>
              <p className="text-2xl font-bold text-green-600">{summary?.complete_deliveries || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Entregas Parciales</p>
              <p className="text-2xl font-bold text-amber-600">{summary?.partial_deliveries || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Cumplimiento Prom.</p>
              <p className="text-2xl font-bold text-purple-600">{summary?.average_compliance || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen de cantidades */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <Package className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-blue-100 text-sm">Total Pedido</p>
          <p className="text-3xl font-bold">{summary?.total_items_requested?.toLocaleString() || 0}</p>
          <p className="text-blue-200 text-xs mt-1">unidades solicitadas</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <CheckCircle2 className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-green-100 text-sm">Total Entregado</p>
          <p className="text-3xl font-bold">{summary?.total_items_delivered?.toLocaleString() || 0}</p>
          <p className="text-green-200 text-xs mt-1">unidades despachadas</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white">
          <TrendingDown className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-red-100 text-sm">Total Faltante</p>
          <p className="text-3xl font-bold">{summary?.total_shortage?.toLocaleString() || 0}</p>
          <p className="text-red-200 text-xs mt-1">unidades no entregadas</p>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de pie: Entregas completas vs parciales */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Estado de Entregas</h3>
          {summary?.total_routes > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={deliveryPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {deliveryPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-slate-400">
              No hay datos para mostrar
            </div>
          )}
        </div>

        {/* Gráfico de barras: Entregas por local */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Entregas por Local</h3>
          {locationBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={locationBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completas" name="Completas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="parciales" name="Parciales" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-slate-400">
              No hay datos para mostrar
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas por local */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-400" />
            Rendimiento por Local
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Local</th>
                <th className="px-4 py-3 text-center font-semibold">Total Rutas</th>
                <th className="px-4 py-3 text-center font-semibold">Completas</th>
                <th className="px-4 py-3 text-center font-semibold">Parciales</th>
                <th className="px-4 py-3 text-center font-semibold">Cumplimiento</th>
                <th className="px-4 py-3 text-right font-semibold">Total Faltante</th>
              </tr>
            </thead>
            <tbody>
              {locationStats.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No hay datos para el período seleccionado
                  </td>
                </tr>
              ) : (
                locationStats.map((loc) => (
                  <tr key={loc.location_id} className="border-b hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {loc.location_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{loc.total_routes}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-green-600 font-semibold">{loc.complete_deliveries}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-amber-600 font-semibold">{loc.partial_deliveries}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        loc.average_compliance >= 95 ? 'bg-green-100 text-green-700' :
                        loc.average_compliance >= 80 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {loc.average_compliance}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {loc.total_shortage > 0 ? (
                        <span className="text-red-600 font-semibold">-{loc.total_shortage}</span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top 10 productos con más faltantes por local */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Top 10 Productos con Más Faltantes por Local
          </h3>
        </div>
        
        {topShortages.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p>¡Excelente! No hay faltantes registrados en el período seleccionado</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {topShortages.map((locReport) => (
              <div key={locReport.location_id} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-tupu-orange-500" />
                  <h4 className="font-semibold text-slate-900">{locReport.location_name}</h4>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    {locReport.top_shortage_items.length} productos con faltantes
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {locReport.top_shortage_items.map((item, index) => (
                    <div 
                      key={item.item_id}
                      className={`p-3 rounded-lg ${
                        index === 0 ? 'bg-red-50 border border-red-200' :
                        index < 3 ? 'bg-amber-50 border border-amber-200' :
                        'bg-slate-50 border border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <span className={`text-xs font-bold ${
                            index === 0 ? 'text-red-600' :
                            index < 3 ? 'text-amber-600' : 'text-slate-500'
                          }`}>
                            #{index + 1}
                          </span>
                          <p className="font-medium text-slate-900 text-sm">{item.item_name}</p>
                          <p className="text-xs text-slate-500">{item.item_code}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-600 font-bold">-{item.total_shortage}</p>
                          <p className="text-xs text-slate-400">{item.shortage_count}x faltó</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Últimas rutas */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Truck className="w-5 h-5 text-slate-400" />
            Últimas Rutas
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha/Hora</th>
                <th className="px-4 py-3 text-left font-semibold">Local</th>
                <th className="px-4 py-3 text-center font-semibold">Productos</th>
                <th className="px-4 py-3 text-center font-semibold">Pedido</th>
                <th className="px-4 py-3 text-center font-semibold">Entregado</th>
                <th className="px-4 py-3 text-center font-semibold">Cumplimiento</th>
                <th className="px-4 py-3 text-left font-semibold">Responsable</th>
              </tr>
            </thead>
            <tbody>
              {recentRoutes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    No hay rutas registradas
                  </td>
                </tr>
              ) : (
                recentRoutes.map((route) => (
                  <tr key={route.id} className="border-b hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {formatDateTime(route.started_at)}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {route.location_name}
                    </td>
                    <td className="px-4 py-3 text-center text-sm">
                      {route.items?.length || 0}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">
                      {route.total_requested}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">
                      {route.total_loaded}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        route.compliance_percentage >= 100 ? 'bg-green-100 text-green-700' :
                        route.compliance_percentage >= 80 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {route.compliance_percentage}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {route.created_by_name}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminLogisticsPage;
